import { useFormStore } from "@/stores/use-form-store"
import FieldInfoIcon from "../../components/field-info"
import { cn } from "../../lib/utils"
import {
	TextInputConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form"
import { Input } from "../ui/input"
import { Description } from "./components/description"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

export const Text = ({ form, field, commonAttributes }: FieldProps) => {
	const { label, description, infoIconText, infoIconType, infoIconRequired } =
		commonAttributes
	const { placeholderText, caseFormatting } = getTextInputAttributes(
		field.attributes,
	)
	const { shouldDisable, verifiedFields } = useFormStore()

	const isVerified = verifiedFields.get(field.field_id) || false
	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			name={field.field_id}
			control={form.control}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<FormControl>
						<Input
							disabled={isVerified || shouldDisable}
							{...formField}
							onChange={e => {
								const formattedValue = caseTransform(
									caseFormatting!,
									e.target.value,
								)
								formField.onChange(formattedValue)
							}}
							placeholder={placeholderText}
						/>
					</FormControl>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getTextInputAttributes(attributes: Attribute[]) {
	const props: {
		placeholderText?: string
		characterLimit?: number
		inputType?: string
		caseFormatting?: string
	} = {}

	attributes.forEach(attr => {
		switch (attr.name) {
			case TextInputConfig.PlaceholderText:
				props.placeholderText = attr.value
				break
			case TextInputConfig.CaseFormatting:
				props.caseFormatting = attr.value
				break
		}
	})

	return props
}

function caseTransform(caseType: string, value: string) {
	let val: string = value

	switch (caseType) {
		case "UPPERCASE":
			val = val.toUpperCase()
			break
		case "LOWERCASE":
			val = val.toLowerCase()
			break
		case "CAPITALIZE":
			val = val.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) // Capitalize each word
			break
	}

	return val
}
